import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skull, Search, X } from 'lucide-react';
import { ProfileLink } from '@/components/ProfileLink';

interface ShameEntry {
    id: number;
    title: string;
    sub: { id: number; display_name: string; avatar_path?: string } | null;
    dom: { id: number; display_name: string; avatar_path?: string } | null;
    opening_balance: string;
    current_balance: string;
    total_paid: string;
    abandoned_at: string | null;
    started_at: string | null;
}

interface ShameResponse {
    data: ShameEntry[];
    meta: { current_page: number; last_page: number };
}

export default function WallOfShamePage() {
    const [search, setSearch] = useState('');

    const { data, isLoading } = useQuery({
        queryKey: ['wall-of-shame', search],
        queryFn: () => api.get<ShameResponse>(`/wall-of-shame${search ? `?search=${encodeURIComponent(search)}` : ''}`),
    });

    const entries = data?.data ?? [];

    return (
        <div className="space-y-6">
            <div className="text-center">
                <h1 className="flex items-center justify-center gap-2 text-2xl font-bold text-red-400">
                    <Skull className="h-6 w-6" />
                    Wall of Shame
                    <Skull className="h-6 w-6" />
                </h1>
                <p className="mt-1 text-sm text-slate-500">
                    Subs who abandoned their debt contracts
                </p>
            </div>

            <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by name or contract title..."
                    className="pl-9"
                />
                {search && (
                    <button
                        onClick={() => setSearch('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                    >
                        <X className="h-4 w-4" />
                    </button>
                )}
            </div>

            {isLoading && (
                <p className="text-center text-sm text-slate-500">Loading...</p>
            )}

            {!isLoading && entries.length === 0 && (
                <Card>
                    <CardContent className="py-12 text-center text-sm text-slate-500">
                        {search ? 'No results match your search.' : 'No abandoned contracts yet. All subs are behaving... for now.'}
                    </CardContent>
                </Card>
            )}

            <div className="space-y-3">
                {entries.map((entry) => (
                    <Card key={entry.id} className="border-red-900/30">
                        <CardContent className="py-4">
                            <div className="flex items-start gap-4">
                                {/* Avatar */}
                                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-red-900/30 text-red-400">
                                    {entry.sub?.avatar_path ? (
                                        <img
                                            src={entry.sub.avatar_path}
                                            alt=""
                                            className="h-12 w-12 rounded-full object-cover"
                                        />
                                    ) : (
                                        <Skull className="h-6 w-6" />
                                    )}
                                </div>

                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                        {entry.sub ? (
                                            <ProfileLink userId={entry.sub.id} displayName={entry.sub.display_name} className="font-semibold text-red-400 hover:underline" />
                                        ) : (
                                            <span className="font-semibold text-red-400">Unknown</span>
                                        )}
                                        <span className="text-sm text-slate-500">abandoned</span>
                                    </div>
                                    <p className="text-sm text-slate-300">{entry.title}</p>
                                    {entry.dom && (
                                        <p className="text-xs text-slate-500">
                                            Dom: <ProfileLink userId={entry.dom.id} displayName={entry.dom.display_name} className="text-violet-400 hover:underline text-xs" />
                                        </p>
                                    )}

                                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
                                        <span className="text-slate-400">
                                            Opening: <span className="text-slate-200">${parseFloat(entry.opening_balance).toFixed(2)}</span>
                                        </span>
                                        <span className="text-slate-400">
                                            Balance at abandonment: <span className="text-red-400 font-medium">${parseFloat(entry.current_balance).toFixed(2)}</span>
                                        </span>
                                        <span className="text-slate-400">
                                            Total paid: <span className="text-slate-200">${parseFloat(entry.total_paid).toFixed(2)}</span>
                                        </span>
                                    </div>

                                    {entry.abandoned_at && (
                                        <p className="mt-1 text-xs text-slate-600">
                                            Abandoned {entry.abandoned_at}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>
        </div>
    );
}
